import React from "react";
import { Link } from "gatsby";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import HelpfulFirstStep from "../components/HelpfulFirstStep";
import TeamPortraitGroup from "../components/TeamPortraitGroup";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";

import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const journeySteps = [
	{
		number: "01",
		title: "Begin with a conversation",
		text:
			"A Family Care Advisor listens to what is changing, what matters at home and what you would like care to make possible.",
		image: "care-advisor-office.png",
		alt: "A Family Care Advisor ready to listen",
		cta: {
			label: "Talk to a Care Advisor",
			to: "/care-consultation/",
		},
	},
	{
		number: "02",
		title: "Build the right care plan",
		text:
			"A Care Manager completes a no-obligation assessment, taking time to understand routines, health needs, personal goals and the home environment.",
		image: "livein-imageclus-05.png",
		alt: "A family having a relaxed conversation together at home",
	},
	{
		number: "03",
		title: "Meet carefully selected carers",
		text:
			"We propose carers whose experience, availability and personality fit the person. You can review their profile and speak before making a decision.",
		image: "approach-steps-img-2.png",
		alt: "A profile showing the experience and interests of a proposed carer",
	},
	{
		number: "04",
		title: "Care begins at home",
		text:
			"Your chosen carer arrives ready, with the care plan, practical arrangements and a warm introduction in place. We stay close through the first days as everyone settles in.",
		image: "approach-steps-img-4.png",
		alt: "An older person smiling as care begins at home",
	},
	{
		number: "05",
		title: "Care that stays connected",
		text:
			"Your carer is never working alone. A Family Care Coordinator and Family Care Manager stay close to the detail, helping care adapt when life changes.",
		image: "approach-steps-img-5.png",
		alt: "An Edyn care team member staying connected with a family",
	},
];

const teamRoles = [
	{
		number: "01",
		title: "Your professional carers",
		text:
			"A main and respite carer provide one-to-one support in the home, guided by the same care plan and personal routines.",
	},
	{
		number: "02",
		title: "Your Family Care Coordinator",
		text:
			"The familiar point of contact who coordinates schedules, travel and the practical details that keep care running smoothly.",
	},
	{
		number: "03",
		title: "Your Family Care Manager",
		text:
			"An experienced care professional who oversees quality, reviews changing needs and supports more complex decisions.",
	},
];

const portalFeatures = [
	"Your care team and schedule",
	"The current care plan and risk assessments",
	"Carer notes and wellness updates",
	"Care statements and payments",
];

export default function Approach() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="How Edyn Live-in Care Works | Edyn Care"
				description="From the first conversation and care assessment to carer matching and ongoing support, see how Edyn helps families arrange fully managed live-in care."
				path="/approach/"
			/>
			<Header />

			<main id="main-content" className="edyn-approach">
				<section className="approach-hero">
					<div className="site-shell approach-hero__grid">
						<div className="approach-hero__copy">
							<p className="eyebrow eyebrow--light">How Edyn works</p>
							<h1>
								<span>Care arranged clearly.</span>
								<em>Managed properly.</em>
								<span>Supported throughout.</span>
							</h1>
							<p className="approach-hero__lead">
								From the first conversation to day-to-day care, a dedicated team
								takes responsibility for making live-in care work well at home.
							</p>
							<div className="button-row">
								<Link className="button button--cream" to="/care-consultation/">
									Arrange a call
								</Link>
								<a className="button button--text-light" href="tel:02039709900">
									Call 020 3970 9900
								</a>
							</div>
						</div>

						<div className="approach-hero__visual">
							<img
								src={`${imagePath}/live-in-image-01.jpg`}
								alt="A professional carer sharing a warm conversation at home"
								width="1327"
								height="1115"
							/>
							<div className="approach-hero__note">
								<span aria-hidden="true">01</span>
								<p>
									<strong>It starts with listening.</strong>
									<br /> No pressure and no obligation.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="approach-proof" aria-label="Edyn care model">
					<div className="site-shell approach-proof__grid">
						<p>
							<strong>CQC regulated</strong>
							<span>Fully managed care, not simply an introduction</span>
						</p>
						<p>
							<strong>A dedicated care team</strong>
							<span>Real people who stay involved throughout care</span>
						</p>
						<div className="approach-proof__trustpilot">
							<span>Independent customer reviews</span>
							<MicroCombo
								classNames="approach-proof__reviews"
								alignment="left"
								width="270px"
							/>
						</div>
					</div>
				</section>

				<section className="approach-intro section-space">
					<div className="site-shell approach-intro__grid">
						<div>
							<p className="eyebrow">A considered approach</p>
							<h2>
								One professional carer in the home. A whole care team behind
								them.
							</h2>
						</div>
						<div className="approach-intro__copy">
							<p>
								Arranging care can feel like a series of unfamiliar decisions.
								Our role is to make each one easier to understand, then manage
								the detail so families do not have to hold everything alone.
							</p>
							<Link className="text-link" to="/liveincare/">
								Understand live-in care <span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
					</div>
				</section>

				<section className="approach-journey section-space section-soft">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Your journey with Edyn</p>
								<h2>Five clear steps, with someone beside you at each one.</h2>
							</div>
							<p>
								Every situation is different. The sequence stays simple, while
								the care itself is shaped around the person and their family.
							</p>
						</div>

						<ol className="approach-journey__list">
							{journeySteps.map((step) => (
								<li className="approach-journey__step" key={step.number}>
									<div className="approach-journey__image">
										<img
											src={`${imagePath}/${step.image}`}
											alt={step.alt}
											loading="lazy"
										/>
									</div>
									<div className="approach-journey__number" aria-hidden="true">
										{step.number}
									</div>
									<div className="approach-journey__copy">
										<h3>{step.title}</h3>
										<p>{step.text}</p>
										{step.cta && (
											<Link className="button button--primary" to={step.cta.to}>
												{step.cta.label}
											</Link>
										)}
									</div>
								</li>
							))}
						</ol>
					</div>
				</section>

				<section className="approach-team section-space section-dark">
					<div className="site-shell">
						<div className="approach-team__heading">
							<div>
								<p className="eyebrow eyebrow--light">People behind the care</p>
								<h2>Your support does not begin and end with the carer.</h2>
							</div>
							<TeamPortraitGroup className="approach-team__portraits" />
						</div>

						<div className="approach-team__roles">
							{teamRoles.map((role) => (
								<article key={role.number}>
									<span>{role.number}</span>
									<h3>{role.title}</h3>
									<p>{role.text}</p>
								</article>
							))}
						</div>
						<div className="approach-team__reviews">
							<p>
								Families can read independent feedback on the care team supporting
								them.
							</p>
							<MicroCombo
								classNames="approach-team__trustpilot approach-team__trustpilot--desktop"
								theme="dark"
								alignment="right"
								width="270px"
							/>
							<MicroCombo
								classNames="approach-team__trustpilot approach-team__trustpilot--mobile"
								theme="dark"
								alignment="left"
								width="270px"
							/>
						</div>
					</div>
				</section>

				<section className="approach-continuity section-space">
					<div className="site-shell approach-continuity__grid">
						<div className="approach-continuity__copy">
							<p className="eyebrow">Consistency that is sustainable</p>
							<h2>Familiar care, planned around people rather than shifts.</h2>
							<p>
								Continuity matters. Your main and respite carers work to the
								same plan and learn the same routines, creating familiarity for
								the person receiving care and proper rest for the people
								providing it.
							</p>
							<Link className="text-link" to="/our-carers/">
								Meet our carers <span aria-hidden="true">&#8594;</span>
							</Link>
						</div>

						<div
							className="approach-rotation"
							aria-label="Example carer rotation"
						>
							<div className="approach-rotation__people">
								<div>
									<img
										src={`${imagePath}/olivia.png`}
										alt="Olivia, example main carer"
										loading="lazy"
									/>
									<p>
										<strong>Olivia</strong>
										<span>Main carer</span>
										Familiar day-to-day support
									</p>
								</div>
								<div>
									<img
										src={`${imagePath}/ayaan.png`}
										alt="Ayaan, example respite carer"
										loading="lazy"
									/>
									<p>
										<strong>Ayaan</strong>
										<span>Respite carer</span>
										Planned, consistent cover
									</p>
								</div>
							</div>
							<div className="approach-rotation__weeks" aria-hidden="true">
								<span>Week 1</span>
								<span>Week 2</span>
								<span>Week 3</span>
								<span>Week 4</span>
								<i className="is-main">
									<img src={`${imagePath}/olivia.png`} alt="" />
								</i>
								<i className="is-main">
									<img src={`${imagePath}/olivia.png`} alt="" />
								</i>
								<i className="is-respite">
									<img src={`${imagePath}/ayaan.png`} alt="" />
								</i>
								<i className="is-main">
									<img src={`${imagePath}/olivia.png`} alt="" />
								</i>
							</div>
							<p className="approach-rotation__note">
								Schedules are agreed around the placement; this is an example of
								how familiar carers can work in rotation.
							</p>
						</div>
					</div>
				</section>

				<section className="approach-portal section-space section-clay">
					<div className="site-shell approach-portal__grid">
						<div className="approach-portal__visual">
							<img
								src={`${imagePath}/tech-image-00.png`}
								alt="The My Edyn portal showing a proposed professional carer"
								loading="lazy"
							/>
						</div>
						<div className="approach-portal__copy">
							<p className="eyebrow">Clear information for families</p>
							<h2>Technology that makes care feel more human, not less.</h2>
							<p>
								The My Edyn portal keeps important information in one place,
								while your care team remains available when you would rather
								speak to someone.
							</p>
							<ul>
								{portalFeatures.map((feature) => (
									<li key={feature}>
										<span aria-hidden="true">&#10003;</span>
										{feature}
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				<HelpfulFirstStep />
			</main>

			<Footer includeCTA={false} />
		</div>
	);
}
